<template>
    <!-- native checkbox is wrapped inside the label. -->
    <label
        class="checkbox-wrapper"
    >
        <input
            ref="checkboxInputRef"
            :id="id"
            v-model="modelValue"
            type="checkbox"
            :name="name ?? ''"
            :disabled="disabled"
            :data-cy="cypressId"
            :indeterminate="indeterminate"
            :aria-label="ariaLabel ?? undefined"
            class="input visually-hidden"
        >

        <div
            class="checkbox"
            aria-hidden="true"
            tabindex="-1"
            @click="onCheckboxClick"
        >
            <CheckIcon
                class="check-icon"
                width="20"
                height="20"
            />
        </div>

        <!-- Span sits inside the wrapping <label>, which already forwards clicks to the input. -->
        <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
        <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
        <span
            class="checkbox-label"
            tabindex="-1"
            @click="onLabelClick"
        >
            <slot />
        </span>
    </label>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import CheckIcon from '../../../images/check.svg?component';

type Props = {
    id?: string,
    name?: string | null | undefined,
    disabled?: boolean,
    autofocus?: boolean,
    indeterminate?: boolean,
    cypressId?: string | null,
    ariaLabel?: string | null,
}

type Emits = {
    (e: 'click', value: Event): void,
}

const modelValue = defineModel<boolean>({ default: false });

const props = withDefaults(
    defineProps<Props>(),
    {
        id: undefined,
        disabled: false,
        name: '',
        autofocus: false,
        indeterminate: false,
        cypressId: null,
        ariaLabel: null,
    },
);

const emit = defineEmits<Emits>();

const checkboxInputRef = ref<HTMLInputElement | null>(null);

onMounted(() => {
    if (props.autofocus) {
        checkboxInputRef.value?.focus();
    }
});

const onCheckboxClick = (event: MouseEvent): void => {
    if (props.disabled) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    modelValue.value = !modelValue.value;
    emit('click', event);
};

const onLabelClick = (event: MouseEvent): void => {
    if (props.disabled) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    modelValue.value = !modelValue.value;
    emit('click', event);
};

</script>

<style lang="scss">
@use '@/styles/abstracts/spacings';
@use '@/styles/abstracts/colors_old';
@use '@/styles/abstracts/variables';

$checkbox-size: 1.25rem;
$checkbox-check-size: 0.875rem;

.checkbox-wrapper {
    position: relative;

    display: inline-flex;

    gap: spacings.$spacing-xs;

    width: fit-content;
    max-width: 100%;

    margin: auto 0;

    pointer-events: none;
}

.checkbox-wrapper .input {
    pointer-events: auto;
    cursor: pointer;
}

.checkbox-wrapper .checkbox-label {
    display: flex;

    align-items: center;

    pointer-events: auto;
    cursor: pointer;
}

.checkbox {
    display: flex;

    flex-shrink: 0;

    align-items: center;
    justify-content: center;

    width: $checkbox-size;
    height: $checkbox-size;

    border: 1px solid var(--theme-color-border-icon-button);
    border-radius: variables.$border-radius-xs;

    transition: border-color 0.3s;

    .check-icon {
        display: none;

        fill: var(--theme-color-surface-primary-default);
    }
}

.checkbox-wrapper .checkbox {
    pointer-events: auto;
    cursor: pointer;
}

.input:checked ~ .checkbox {
    background-color: var(--theme-color-surface-accent-primary);
    border: 0;

    .check-icon {
        display: block;
    }
}

.input:indeterminate ~ .checkbox {
    background: none;
    border: variables.$border-radius-xs solid var(--theme-color-surface-accent-primary);

    &::after {
        display: block;

        width: $checkbox-check-size;
        height: $checkbox-check-size;

        content: '';

        background-color: var(--theme-color-text-primary-light);
        border-radius: variables.$border-radius-xxxs;
    }
}

.input:disabled ~ .checkbox {
    cursor: not-allowed;

    background-color: var(--theme-color-surface-secondary-default);
    border: 1px solid var(--theme-color-border-icon-button);
}

.input:focus-visible ~ .checkbox {
    border-color: var(--theme-color-icon-accent-1);
}

@media (hover: hover) {
    .input:not(:disabled):hover ~ .checkbox {
        border-color: var(--theme-color-icon-accent-1);
    }
}

.checkbox-wrapper:has(input:disabled) .checkbox {
    cursor: not-allowed;
}

.input:disabled:checked ~ .checkbox {
    background-color: var(--theme-color-icon-quatriary);
    border: 0;
}

.input:checked:focus-visible ~ .checkbox {
    background-color: var(--theme-color-border-accent);
}
</style>
